import { Subscription } from 'rxjs';
import { EwsRefresh } from '../../../../services/ews-refresh';
import { OnDestroy, OnChanges, SimpleChanges, Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { lastValueFrom, RmsMessageService, RmsPulldownService } from '@services';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { EwsDataService, EwsFilters, dateOnly, monthLabel } from '../../../../services/ews-data.service';
import { downloadCsv } from '../../../../services/swap-data.service';
import { GroupAnalysisComponent, GroupAnalysisData } from './group-analysis/group-analysis.component';

// =====================================================================
// EarlyWarningComponent — Scope 6.4 EWS Summary (S1) + Spot & Transfer (S3)
// ---------------------------------------------------------------------
// One row per coffee group: burn, stock, months covered, depletion month,
// order-by date, need before crop, spot status, then the projected
// closing bags month by month (red = short, gold outline = new-crop month).
// Picking a warehouse in the filter bar = the Spot & Transfer view (S3).
// Data: GET /api/v1/ews/summary  (proc ews_get_summary)
// =====================================================================

interface CoverageCard {
  label: string;
  status: string | null;     // null = All
  cssClass: string;
}

@Component({
  selector: 'app-early-warning',
  imports: [RmsModule],
  templateUrl: './early-warning.component.html',
  styleUrl: './early-warning.component.scss'
})
export class EarlyWarningComponent implements OnInit, OnDestroy {
  private refresh = new EwsRefresh();
  private liveSubscription?: Subscription;
  UpdatedAt: Date | null = null;
  ngOnDestroy() { this.refresh.destroy(); this.liveSubscription?.unsubscribe(); }

  // rms-table has no ngOnChanges on columns → it renders only once the
  // month columns are built from the first load (known rms-table quirk).
  Loaded = false;
  Loading = false;

  // ---- filter bar ----
  Filters: EwsFilters = {
    BurnRange: 3,
    WarehouseID: null,
    SafsValue: 10,
    SafsType: 'PCT',
    CropOption: 2,
    VolatileMarket: false,
    ExcludeFireSales: false,
    DaysInAdj: 0,
    IncludeInactive: false
  };

  BurnRangeOptions = [
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 12, label: '12 months' }
  ];
  // value '' = all warehouses (rms-select's placeholder option uses null)
  WarehouseOptions: { value: any; label: string }[] = [{ value: '', label: 'All warehouses' }];
  SafsTypeOptions = [
    { value: 'PCT', label: '% of burn' },
    { value: 'BAGS', label: 'Bags / month' }
  ];
  CropOptions = [
    { value: 1, label: 'Crop #1 only' },
    { value: 2, label: 'Crop #1 + #2' }
  ];
  SelectedWarehouse: any = '';

  // ---- results ----
  RunInfo: any = null;
  AllRows: any[] = [];
  FilteredRows: any[] = [];      // real array — rms-table's data setter fires only on change
  Columns: Column[] = [];
  MonthKeys: string[] = [];      // 'M1'…'M24'

  CoverageCards: CoverageCard[] = [
    { label: 'All groups', status: null, cssClass: 'card-all' },
    { label: 'Immediate Action', status: 'Immediate Action', cssClass: 'card-immediate' },
    { label: 'Purchase Soon', status: 'Purchase Soon', cssClass: 'card-soon' },
    { label: 'Watch List', status: 'Watch List', cssClass: 'card-watch' },
    { label: 'Covered', status: 'Covered', cssClass: 'card-covered' },
    { label: 'No Burn', status: 'No Burn', cssClass: 'card-noburn' }
  ];
  ActiveCoverage: string | null = null;
  Counts: Record<string, number> = {};

  Buttons: HeaderButton[] = [
    { label: 'Export CSV', onAction: () => this.exportCsv() }
  ];

  constructor(
    private ews: EwsDataService,
    private msg: RmsMessageService,
    private pulldownService: RmsPulldownService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    await this.loadOptions();
    await this.run();
    this.liveSubscription = this.ews.changes$.subscribe(() => this.queueRefresh());
  }

  // ---------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------

  /** Warehouses + the saved defaults (burn range, SAFS, crop option) */
  async loadOptions() {
    try {
      const opt = await lastValueFrom(this.ews.getOptions());
      this.WarehouseOptions = [
        { value: '', label: 'All warehouses' },
        ...(opt?.Warehouses ?? []).map((w: any) => ({ value: w.WarehouseID, label: w.LocationShortName || w.LocationName }))
      ];

      const setting = (key: string) => (opt?.Settings ?? []).find((s: any) => s.SettingKey === key)?.SettingValue;
      const burn = Number(setting('DefaultBurnRange'));
      const safs = Number(setting('DefaultSafsValue'));
      const safsType = setting('DefaultSafsType');
      const crop = Number(setting('DefaultCropOption'));
      if ([3, 6, 12].includes(burn)) this.Filters.BurnRange = burn as any;
      if (!Number.isNaN(safs) && setting('DefaultSafsValue') != null) this.Filters.SafsValue = safs;
      if (safsType === 'PCT' || safsType === 'BAGS') this.Filters.SafsType = safsType;
      if (crop === 1 || crop === 2) this.Filters.CropOption = crop;
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load the EWS options.');
    }
  }

  /** Run the forecast with the current filters */
  /** the filter values the table on screen was run with (null = not run yet) */
  private LastRunKey: string | null = null;

  private filterKey(): string {
    const f = this.Filters;
    return JSON.stringify([f.BurnRange, String(this.SelectedWarehouse ?? ''), String(f.SafsValue ?? ''), f.SafsType,
    f.CropOption, String(f.DaysInAdj ?? ''), !!f.VolatileMarket, !!f.ExcludeFireSales, !!f.IncludeInactive]);
  }

  /** true when a filter was changed after the last Run — shows the "click Run" heads-up */
  get FiltersChanged(): boolean {
    return this.LastRunKey !== null && !this.Loading && this.filterKey() !== this.LastRunKey;
  }

  queueRefresh() { this.refresh.schedule(() => this.run()); }

  async run() {
    const revision = this.refresh.begin();
    const runKey = this.filterKey();
    this.Loading = true;
    this.cdr.markForCheck();
    try {
      this.Filters.WarehouseID = this.SelectedWarehouse === '' ? null : Number(this.SelectedWarehouse);
      const result = await lastValueFrom(this.ews.getSummary(this.Filters));
      if (!this.refresh.current(revision)) return;
      this.RunInfo = result?.RunInfo ?? null;

      // months come back long (GroupID × MonthIndex) → pivot onto the group rows
      const months = result?.Months ?? [];
      const byGroup = new Map<number, any[]>();
      for (const m of months) {
        if (!byGroup.has(m.GroupID)) byGroup.set(m.GroupID, []);
        byGroup.get(m.GroupID)!.push(m);
      }

      // build the month columns once (they only change when the day changes)
      if (!this.Loaded) {
        const first = months.filter((m: any) => m.GroupID === months[0]?.GroupID);
        this.MonthKeys = first.map((m: any) => `M${m.MonthIndex}`);
        this.Columns = this.createColumns(first);
      }

      this.AllRows = (result?.Groups ?? []).map((g: any) => {
        const row: any = {
          ...g,
          DisplayName: g.ParentGroupName ? `${g.ParentGroupName} › ${g.GroupName}` : g.GroupName,
          BurnUsed: round(g.BurnUsed, 1),
          GoalBags: round(g.GoalBags, 1),
          SpotOnHand: round(g.SpotOnHand, 0),
          SpotReserved: round(g.SpotReserved, 0),
          SpotUnsold: round(g.SpotUnsold, 0),
          ForwardBags: round(g.ForwardBags, 0),
          ForwardUnsold: round(g.ForwardUnsold, 0),
          NeedBeforeCrop: round(g.NeedBeforeCrop, 0),
          MaxDeficit10: round(g.MaxDeficit10, 0),
          NextArrival: dateOnly(g.NextArrival),
          Crop1Arrival: dateOnly(g.Crop1Arrival),
          Crop2Arrival: dateOnly(g.Crop2Arrival),
          DepletionMonth: dateOnly(g.DepletionMonth),
          OrderByDate: dateOnly(g.OrderByDate),
          _crop: {} as Record<string, boolean>
        };
        for (const m of byGroup.get(g.GroupID) ?? []) {
          const key = `M${m.MonthIndex}`;
          row[key] = round(m.ClosingBags, 0);
          row._crop[key] = !!(m.IsCrop1Month || m.IsCrop2Month);
        }
        return row;
      });

      this.Counts = {};
      for (const r of this.AllRows) this.Counts[r.CoverageStatus] = (this.Counts[r.CoverageStatus] ?? 0) + 1;
      this.applyFilter();
      this.Loaded = true;
      this.LastRunKey = runKey;
      this.UpdatedAt = new Date();
    } catch (err: any) {
      if (this.refresh.current(revision)) this.msg.error(err?.error?.error ?? 'Could not run the Early Warning System.');
    } finally {
      this.Loading = false;
      this.cdr.markForCheck();
    }
  }

  applyFilter() {
    this.FilteredRows = this.ActiveCoverage === null
      ? [...this.AllRows]
      : this.AllRows.filter(r => r.CoverageStatus === this.ActiveCoverage);
    this.cdr.markForCheck();
  }

  selectCoverage(card: CoverageCard) {
    this.ActiveCoverage = card.status;
    this.applyFilter();
  }

  countFor(card: CoverageCard): number {
    return card.status === null ? this.AllRows.length : (this.Counts[card.status] ?? 0);
  }

  get IsSpotView(): boolean {
    return this.SelectedWarehouse !== '' && this.SelectedWarehouse !== null;
  }

  // ---------------------------------------------------------------------
  // Table
  // ---------------------------------------------------------------------

  createColumns(firstGroupMonths: any[]): Column[] {
    const cols: Column[] = [
      { key: 'Section', label: 'Section', type: 'select', SearchType: 'select', hidden: false, width: 6 },
      {
        key: 'DisplayName', label: 'Coffee Group', hidden: false, width: 14, align: 'left',
        clickable: true, onActionKey: 'openGroup', onAction: (row: any) => this.openGroup(row)
      },
      { key: 'BurnUsed', label: 'Burn / Mo', type: 'number', hidden: false },
      { key: 'GoalBags', label: 'Goal / Mo', type: 'number', hidden: false },
      { key: 'SpotOnHand', label: 'Spot On Hand', type: 'number', hidden: false },
      { key: 'SpotUnsold', label: 'Spot Unsold', type: 'number', hidden: false },
      { key: 'ForwardUnsold', label: 'Fwd Unsold', type: 'number', hidden: false },
      {
        key: 'MonthsCovered', label: 'Months Covered', type: 'number', fixed: 1, hidden: false,
        cellClass: (r: any) => coverageClass(r.CoverageStatus)
      },
      {
        key: 'CoverageStatus', label: 'Coverage', type: 'select', SearchType: 'select', hidden: false,
        cellClass: (r: any) => coverageClass(r.CoverageStatus)
      },
      { key: 'DepletionMonth', label: 'Depletion', type: 'date', SearchType: 'date', hidden: false },
      { key: 'OrderByDate', label: 'Order By', type: 'date', SearchType: 'date', hidden: false },
      {
        key: 'NeedBeforeCrop', label: 'Need Before Crop', type: 'number', hidden: false,
        cellClass: (r: any) => (r.NeedBeforeCrop > 0 ? 'ews-neg-text' : '')
      },
      { key: 'Crop1Arrival', label: 'New Crop', type: 'date', SearchType: 'date', hidden: false },
      {
        key: 'SpotStatus', label: 'Spot Status', type: 'select', SearchType: 'select', hidden: false,
        cellClass: (r: any) => (r.SpotStatus === 'C' ? 'ews-ok-text' : 'ews-neg-text')
      },
      {
        key: 'ActionNeeded', label: 'Action (10 mo)', hidden: false, align: 'left', width: 10,
        cellClass: (r: any) => (r.MaxDeficit10 > 0 ? 'ews-nowrap ews-neg-text' : 'ews-nowrap')
      },
      // hidden by default — turn on with "customize"
      { key: 'Burn3', label: 'Burn 3', type: 'number', fixed: 1, hidden: true },
      { key: 'Burn6', label: 'Burn 6', type: 'number', fixed: 1, hidden: true },
      { key: 'Burn12', label: 'Burn 12', type: 'number', fixed: 1, hidden: true },
      { key: 'SpotReserved', label: 'Spot Reserved', type: 'number', hidden: true },
      { key: 'ForwardBags', label: 'Fwd Bags', type: 'number', hidden: true },
      { key: 'NextArrival', label: 'Next Arrival', type: 'date', SearchType: 'date', hidden: true },
      { key: 'Crop2Arrival', label: 'Crop #2', type: 'date', SearchType: 'date', hidden: true },
      { key: 'FreightDays', label: 'Freight Days', type: 'number', hidden: true },
      { key: 'SkuCount', label: 'SKUs', type: 'number', hidden: true },
      { key: 'GroupName', label: 'Group', hidden: true },
      { key: 'ParentGroupName', label: 'Parent Group', type: 'select', SearchType: 'select', hidden: true },
    ];

    // month columns: closing bags at the end of each month
    // first 12 shown, 13–24 available through "customize"
    for (const m of firstGroupMonths) {
      const key = `M${m.MonthIndex}`;
      cols.push({
        key,
        label: monthLabel(m.MonthStart),
        type: 'number',
        hidden: m.MonthIndex > 12,
        removeSort: false,
        cellClass: (r: any) => monthClass(r[key], r.GoalBags, r._crop?.[key])
      });
    }
    return cols;
  }

  // ---------------------------------------------------------------------
  // Group Analysis pulldown (S2) — same filters as the Summary run, but
  // always all warehouses (the pulldown has its own warehouse picker)
  // ---------------------------------------------------------------------

  openGroup(row: any) {
    const data: GroupAnalysisData = {
      GroupIDs: [row.GroupID],
      Filters: { ...this.Filters, WarehouseID: null, GroupIDs: null, IncludeInactive: false }
    };
    this.pulldownService.open(GroupAnalysisComponent, {
      data,
      title: `Group Analysis — ${row.DisplayName}`,
      width: '92vw',
      maxWidth: '98vw',
      top: '0',
      maxHeight: '94vh',
      minHeight: '70vh'
    });
  }

  // ---------------------------------------------------------------------
  // Export — what's on screen (current coverage filter), all 24 months
  // ---------------------------------------------------------------------

  exportCsv() {
    const monthCols = this.Columns.filter(c => c.key.startsWith('M') && /^M\d+$/.test(c.key));
    const rows = this.FilteredRows.map(r => {
      const out: any = {
        Section: r.Section, ParentGroup: r.ParentGroupName ?? '', Group: r.GroupName,
        BurnPerMonth: r.BurnUsed, GoalPerMonth: r.GoalBags,
        SpotOnHand: r.SpotOnHand, SpotReserved: r.SpotReserved, SpotUnsold: r.SpotUnsold,
        ForwardBags: r.ForwardBags, ForwardUnsold: r.ForwardUnsold,
        MonthsCovered: r.MonthsCovered, Coverage: r.CoverageStatus,
        DepletionMonth: r.DepletionMonth ?? '', OrderBy: r.OrderByDate ?? '',
        NeedBeforeCrop: r.NeedBeforeCrop, NewCrop: r.Crop1Arrival ?? '',
        SpotStatus: r.SpotStatus, Action: r.ActionNeeded
      };
      for (const c of monthCols) out[(c as any).label] = r[c.key];
      return out;
    });
    const wh = this.IsSpotView ? `-wh${this.SelectedWarehouse}` : '';
    downloadCsv(rows, `ews-summary${wh}-${new Date().toISOString().substring(0, 10)}.csv`);
  }
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function round(v: any, digits: number): number | null {
  if (v === null || v === undefined || v === '') return null;
  const f = Math.pow(10, digits);
  return Math.round(Number(v) * f) / f;
}

/** Month cell colour: red = short, amber = less than one month's goal left */
function monthClass(closing: number | null, goal: number | null, isCrop: boolean): string {
  const classes: string[] = [];
  if (closing !== null && closing < 0) classes.push('ews-cell-neg');
  else if (closing !== null && goal && closing < goal) classes.push('ews-cell-low');
  if (isCrop) classes.push('ews-cell-crop');
  return classes.join(' ');
}

function coverageClass(status: string): string {
  switch (status) {
    case 'Immediate Action': return 'ews-neg-text';
    case 'Purchase Soon': return 'ews-warn-text';
    case 'Watch List': return 'ews-watch-text';
    case 'Covered': return 'ews-ok-text';
    default: return '';
  }
}
